import { useEffect, useMemo, useRef, useState } from 'react';
import type { LineId, NetworkGraph, Occupancy } from '@mrt/graph';
import type { Lang } from '../lib/i18n.js';
import { usePanZoom } from '../lib/usePanZoom.js';
import { measureLabel } from '../lib/measureText.js';

interface Props {
  graph: NetworkGraph;
  occupancies: Occupancy[];
  visibleLines: Set<LineId>;
  lang: Lang;
  clockOffsetMs: number;
  /** When false the animation loop is torn down entirely (backgrounded tab). */
  animate: boolean;
  /** Dim every marker when the data behind them is no longer fresh. */
  dimmed: boolean;
}

/** Gentle acceleration out of a station and deceleration into the next one. */
function ease(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/** Station labels are drawn at this size on screen, whatever the zoom. */
const LABEL_CSS_PX = 12;

export function MapCanvas({ graph, occupancies, visibleLines, lang, clockOffsetMs, animate, dimmed }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const markerRefs = useRef(new Map<string, SVGGElement>());
  const occRef = useRef<Occupancy[]>(occupancies);
  useEffect(() => {
    occRef.current = occupancies;
  }, [occupancies]);

  const { transform, scale, handlers, reset } = usePanZoom(svgRef, graph.viewBox);

  /**
   * User units per CSS pixel. The viewBox is scaled to fit the plate, so
   * anything that must hold a fixed on-screen size — the labels — has to be
   * divided by this or it renders at a third of its nominal size.
   */
  const [unitsPerPx, setUnitsPerPx] = useState(1);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = () => {
      const r = svg.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      const fit = Math.min(r.width / graph.viewBox.width, r.height / graph.viewBox.height);
      if (fit > 0) setUnitsPerPx(1 / fit);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [graph.viewBox.width, graph.viewBox.height]);

  const lineById = useMemo(() => new Map(graph.lines.map((l) => [l.id, l])), [graph]);

  /** Polylines per line, skipping any segment the graph does not know. */
  const linework = useMemo(() => {
    const out: Array<{ lineId: LineId; d: string }> = [];
    for (const [lineId, paths] of Object.entries(graph.linePaths) as Array<[LineId, string[][]]>) {
      for (const path of paths) {
        const points = path
          .map((id) => graph.stations[id])
          .filter((s): s is NonNullable<typeof s> => Boolean(s))
          .map((s) => `${s.x},${s.y}`);
        if (points.length > 1) out.push({ lineId, d: `M${points.join(' L')}` });
      }
    }
    return out;
  }, [graph]);

  const ranked = useMemo(() => {
    const termini = new Set<string>();
    for (const route of graph.routes) {
      const first = route.stations[0];
      const last = route.stations[route.stations.length - 1];
      if (first) termini.add(first);
      if (last) termini.add(last);
    }
    // Interchanges first, then termini, then the rest: when labels compete for
    // room the more useful one should win.
    return Object.values(graph.stations)
      .map((s) => ({ station: s, rank: s.interchange ? 0 : termini.has(s.id) ? 1 : 2 }))
      .sort((a, b) => a.rank - b.rank || a.station.id.localeCompare(b.station.id));
  }, [graph]);

  /**
   * Labels hold a constant on-screen size, so zooming in spreads the geometry
   * apart and makes room for more of them. Anything that would overlap an
   * already-placed label is dropped — an unreadable pile-up in the city centre
   * is worse than fewer names.
   */
  const labelled = useMemo(() => {
    const fontPx = (LABEL_CSS_PX * unitsPerPx) / scale;
    const lineH = fontPx * 1.3;
    const gap = fontPx * 0.5;
    const placed: Array<[number, number, number, number]> = [];
    const out: Array<{ station: (typeof ranked)[number]['station']; fontPx: number; flip: boolean }> = [];
    for (const { station: s, rank } of ranked) {
      if (rank === 2 && scale < 1.6) continue;
      const text = lang === 'zh' ? s.name.zh : s.name.en;
      const w = measureLabel(text, LABEL_CSS_PX) * unitsPerPx;
      // Near the right edge the label would run off the plate, so hang it left.
      const flip = s.x + w + fontPx > graph.viewBox.width;
      const x0 = flip ? s.x - fontPx * 0.75 - w : s.x + fontPx * 0.75;
      const y0 = s.y - lineH / 2;
      const box: [number, number, number, number] = [x0 - gap, y0 - gap, x0 + w + gap, y0 + lineH + gap];
      const hit = placed.some((p) => box[0] < p[2] && box[2] > p[0] && box[1] < p[3] && box[3] > p[1]);
      if (hit) continue;
      placed.push(box);
      out.push({ station: s, fontPx, flip });
    }
    return out;
  }, [ranked, scale, lang, unitsPerPx, graph.viewBox.width]);

  const visibleOccupancies = useMemo(
    () => occupancies.filter((o) => visibleLines.has(o.lineId)),
    [occupancies, visibleLines],
  );

  // Positions are updated imperatively: at ~100 markers and 30fps, re-rendering
  // React on every frame is the difference between smooth and not.
  useEffect(() => {
    if (!animate) return;
    let raf = 0;
    let lastFrame = 0;
    const FRAME_MS = 1000 / 30;

    const step = (ts: number) => {
      raf = requestAnimationFrame(step);
      if (ts - lastFrame < FRAME_MS) return;
      lastFrame = ts;

      const serverNow = Date.now() + clockOffsetMs;
      for (const occ of occRef.current) {
        const el = markerRefs.current.get(occ.key);
        if (!el) continue;
        const from = graph.stations[occ.from];
        const to = graph.stations[occ.to];
        if (!from || !to) continue;
        const elapsed = (serverNow - occ.startedAt) / 1000;
        // Clamped: a marker never renders past a station it was not observed to reach.
        const raw = occ.expectedSec > 0 ? Math.min(1, Math.max(0, elapsed / occ.expectedSec)) : 1;
        const p = ease(raw);
        el.setAttribute('transform', `translate(${from.x + (to.x - from.x) * p} ${from.y + (to.y - from.y) * p})`);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [animate, clockOffsetMs, graph]);

  return (
    <div className="map-shell" onDoubleClick={reset}>
      <svg
        ref={svgRef}
        className="map-svg"
        viewBox={`0 0 ${graph.viewBox.width} ${graph.viewBox.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={lang === 'zh' ? '臺北捷運即時路網圖' : 'Taipei Metro live network map'}
        {...handlers}
      >
        <defs>
          <filter id="lineGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="trainGlow" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g transform={transform}>
          <g filter="url(#lineGlow)" opacity={0.9}>
            {linework.map(({ lineId, d }, i) => (
              <path
                key={`${lineId}-${i}`}
                d={d}
                fill="none"
                stroke={lineById.get(lineId)?.colorOnDark ?? '#888'}
                strokeWidth={visibleLines.has(lineId) ? 7 : 3}
                strokeOpacity={visibleLines.has(lineId) ? 1 : 0.18}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
          </g>

          <g>
            {Object.values(graph.stations).map((s) => (
              <circle
                key={s.id}
                cx={s.x}
                cy={s.y}
                r={s.interchange ? 7 : 4.5}
                fill={s.interchange ? '#0b1020' : '#dfe6f5'}
                stroke={s.interchange ? '#ffffff' : 'rgba(255,255,255,0.55)'}
                strokeWidth={s.interchange ? 3.5 : 1.5}
              />
            ))}
          </g>

          <g className="station-labels">
            {labelled.map(({ station: s, fontPx, flip }) => (
              <text
                key={s.id}
                x={flip ? s.x - fontPx * 0.75 : s.x + fontPx * 0.75}
                y={s.y + fontPx * 0.36}
                textAnchor={flip ? 'end' : 'start'}
                className="station-label"
                style={{ fontSize: fontPx, strokeWidth: fontPx * 0.27 }}
              >
                {lang === 'zh' ? s.name.zh : s.name.en}
              </text>
            ))}
          </g>

          <g opacity={dimmed ? 0.45 : 1}>
            {visibleOccupancies.map((occ) => {
              const from = graph.stations[occ.from];
              const color = lineById.get(occ.lineId)?.colorOnDark ?? '#fff';
              return (
                <g
                  key={occ.key}
                  ref={(el) => {
                    if (el) markerRefs.current.set(occ.key, el);
                    else markerRefs.current.delete(occ.key);
                  }}
                  transform={`translate(${from?.x ?? 0} ${from?.y ?? 0})`}
                  filter="url(#trainGlow)"
                >
                  <circle r={9} fill={color} stroke="#f7fbff" strokeWidth={3} />
                </g>
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
}
